import { CaretDown, Info, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { ClientPriceCalculator } from "@/components/pricing/ClientPriceCalculator";
import { ServicePriceLimitHint } from "@/components/pricing/ServicePriceLimitHint";
import { useServicePriceError } from "@/hooks/useServicePriceLimits";
import { usePricingConfig } from "@/hooks/usePricingConfig";
import { formatCurrency } from "@/lib/format";
import {
  serviceSaveBreakdown,
  serviceSaveGrossPrice,
  typedOldClientPriceAsBase,
  type ServiceSavedBaseline,
} from "@/lib/service-price-save";

interface Props {
  price: number | null;
  onPriceChange: (v: number | null) => void;
  baseline: ServiceSavedBaseline;
}

/**
 * Cijena usluge u onboardingu. Terapeut upisuje iznos bez PDV-a, a odmah do polja
 * vidi cijenu koju klijent plaća. Migrirani terapeut zna samo tu cijenu za klijenta
 * (npr. 81.90), pa mu se ona pokaže uz napomenu da je 70.00 isti iznos bez PDV-a; ako
 * ipak upiše 81.90 u polje, upozorenje nudi povratak na staru cijenu jednim klikom.
 */
export function ServicePriceField({ price, onPriceChange, baseline }: Props) {
  const pricingConfig = usePricingConfig();
  const config = pricingConfig.config;
  // Ista funkcija koja odlučuje šta se sprema: netaknuta cijena prikazuje sirovi
  // spremljeni bruto, pa prikaz i spremljena vrijednost nikad ne odstupaju.
  const clientPrice = price === null || !config ? null : serviceSaveGrossPrice(price, baseline, config);
  // Raspon je zadan za cijenu bez PDV-a, tj. za broj u polju.
  const priceError = useServicePriceError(price, baseline.base);
  const therapistAmount = config && price ? serviceSaveBreakdown(price, baseline, config).therapistAmount : null;

  const oldClientPrice = baseline.gross;
  const typedOldPrice = typedOldClientPriceAsBase(price, baseline);
  const unchanged = price !== null && baseline.base !== null && Math.abs(price - baseline.base) < 0.005;

  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-4">
      <div className="grid items-start gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="svc-price">Cijena bez PDV-a</Label>
          <CurrencyInput
            id="svc-price"
            className="rounded-md bg-background"
            value={price}
            onChange={onPriceChange}
            aria-invalid={Boolean(priceError) || typedOldPrice}
          />
          <ServicePriceLimitHint price={price} savedPrice={baseline.base} />
        </div>
        <div className="rounded-lg bg-primary/10 p-3">
          <span className="block text-xs font-medium text-primary">Klijent plaća (sa PDV-om)</span>
          <span className="block text-2xl font-bold tabular-nums text-primary">
            {clientPrice !== null ? formatCurrency(clientPrice) : "–"}
          </span>
          {therapistAmount !== null && (
            <span className="block text-xs text-muted-foreground">
              Tebi ostaje {formatCurrency(therapistAmount)} po seansi
            </span>
          )}
        </div>
      </div>

      {typedOldPrice && oldClientPrice !== null && baseline.base !== null ? (
        <div className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex gap-2">
            <Warning weight="fill" className="mt-0.5 size-4 shrink-0" />
            <span>
              <b>{formatCurrency(oldClientPrice)}</b> je tvoja dosadašnja cijena <b>sa</b> PDV-om. Ako je upišeš
              ovdje, PDV se doda još jednom i klijent plaća {clientPrice !== null ? formatCurrency(clientPrice) : "više"}.
            </span>
          </p>
          <Button type="button" size="sm" variant="outline" className="shrink-0 bg-white" onClick={() => onPriceChange(baseline.base)}>
            Vrati na {formatCurrency(baseline.base)}
          </Button>
        </div>
      ) : oldClientPrice !== null ? (
        <p className="flex gap-2 text-sm text-muted-foreground">
          <Info weight="fill" className="mt-0.5 size-4 shrink-0 text-primary" />
          {unchanged ? (
            <span>
              Klijenti su dosad plaćali <b className="text-foreground">{formatCurrency(oldClientPrice)}</b>. To je
              ista cijena, u polje se samo upisuje bez PDV-a. Ako ne mijenjaš cijenu, ništa ne diraj.
            </span>
          ) : (
            <span>
              Klijenti su dosad plaćali <b className="text-foreground">{formatCurrency(oldClientPrice)}</b>, sa ovom
              izmjenom plaćaće {clientPrice !== null ? <b className="text-foreground">{formatCurrency(clientPrice)}</b> : "novu cijenu"}.
            </span>
          )}
        </p>
      ) : null}

      <details className="group">
        <summary className="flex cursor-pointer list-none items-center gap-1.5 text-sm font-medium text-primary [&::-webkit-details-marker]:hidden">
          Kako se računa cijena?
          <CaretDown className="size-3.5 transition-transform group-open:rotate-180" />
        </summary>
        <div className="mt-3">
          <ClientPriceCalculator amount={price} pricing={pricingConfig} baseline={baseline} />
        </div>
      </details>
    </section>
  );
}
